import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSandwich, faWave } from '@fortawesome/pro-solid-svg-icons';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Store } from '@ngrx/store';
import { Subject, take } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ODataServiceFactory } from 'angular-odata';
import {
  CoaterLicense,
  CoatingLine,
  Contact,
  DocumentDownloadToken,
  LineExtension,
  PretreatmentType,
  ProductionSite,
  WorkflowDocument,
} from '@workspace-app/odatabackend/QualicoatBackendModel';
import { CertificateTokensComponent } from './certificate-tokens.component';
import { LanguageDropdownComponent } from '../../../../shared/components/language-dropdown/language-dropdown.component';
import { DocumentTypes, EntityTypes } from '../../../../shared/constants/document-types';
import { PermissionService } from '../../../../shared/services/permission.service';
import { User } from '../../../../shared/interfaces/user.interface';
import { selectUser } from '../../../../state/global';
import {
  ActionMenuItem,
  ActionsMenuComponent,
} from '../../../../shared/components/actions-menu/actions-menu.component';
import { DocumentService } from '../../../../shared/services/document.service';
import { LicensesService } from '../../services/licenses.service';
import { ProductionSiteDataService } from '../../../../crm/shared/data-services/production-site-data.service';
import { BillsTableComponent } from '../../../../crm/pages/organisation/shared/bills-table/bills-table.component';

@Component({
  selector: 'app-license-view',
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    TagModule,
    DividerModule,
    TableModule,
    TooltipModule,
    ConfirmDialogModule,
    ProgressSpinner,
    AccordionModule,
    FontAwesomeModule,
    ActionsMenuComponent,
    CertificateTokensComponent,
    LanguageDropdownComponent,
    BillsTableComponent,
  ],
  providers: [DatePipe, ConfirmationService],
  templateUrl: './license-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./license-view.component.scss'],
})
export class LicenseViewComponent implements OnInit, OnDestroy {
  // Signal input for the license
  license = input<CoaterLicense | undefined>(undefined);

  private dialogService = inject(DialogService);
  private config = inject(DynamicDialogConfig, { optional: true });
  private permissionService = inject(PermissionService);
  private store = inject(Store);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private datePipe = inject(DatePipe);
  private documentService = inject(DocumentService);
  private odataFactory = inject(ODataServiceFactory);
  private licensesService = inject(LicensesService);
  private productionSiteDataService = inject(ProductionSiteDataService);
  private destroy$ = new Subject<void>();

  certificateTokens: DocumentDownloadToken[] = [];
  currentUser = signal<User | undefined>(undefined);

  // Icons for extensions
  faSandwich = faSandwich;
  faWave = faWave;

  // Signals for data
  certificates = signal<WorkflowDocument[]>([]);
  latestCertificate = computed(() => this.certificates()[0] ?? null);
  olderCertificates = computed(() => this.certificates().slice(1));
  expandedCertificateTokens = signal<string | null>(null);
  regeneratingCertificate = signal(false);

  // Signal to hold production site coating lines
  productionSiteLines = signal<CoatingLine[]>([]);

  // Computed: line extensions from license
  lineExtensions = computed(() => this.license()?.lineExtensions || []);

  // Computed action menu items
  actionMenuItems = computed(() => this.buildActionMenuItems());

  // Computed signal to detect mismatch between production site lines and endorsed lines
  linesMismatch = computed(() => {
    const endorsed = this.lineExtensions();
    const siteLines = this.productionSiteLines();

    if (siteLines.length === 0) {
      return null; // Still loading or no lines on production site
    }

    const endorsedLineIds = new Set(endorsed.map((le: LineExtension) => le.lineId));
    const siteLineIds = new Set(siteLines.map((line) => line.id));
    const siteLinesMap = new Map(siteLines.map((line) => [line.id, line]));

    // Find lines that are on production site but not endorsed
    const missingEndorsements = siteLines.filter((line) => !endorsedLineIds.has(line.id));

    // Find lines that are endorsed but no longer exist on production site
    const orphanedEndorsements = endorsed.filter((le: LineExtension) => !siteLineIds.has(le.lineId));

    // Find lines where designation or flags have changed on production site
    const outdatedEndorsements = endorsed.filter((le: LineExtension) => {
      const siteLine = siteLinesMap.get(le.lineId);
      if (!siteLine) return false; // Already handled by orphanedEndorsements
      return (
        le.designation !== siteLine.designation ||
        le.pretreatment !== siteLine.pretreatment ||
        le.seaside !== (siteLine.seaside ?? false) ||
        le.postforming !== (siteLine.postforming ?? false)
      );
    });

    if (missingEndorsements.length > 0 || orphanedEndorsements.length > 0 || outdatedEndorsements.length > 0) {
      return {
        missingEndorsements,
        orphanedEndorsements,
        outdatedEndorsements,
        siteLinesMap,
      };
    }

    return null; // No mismatch
  });

  // Computed: endorsement count calculated from line configurations
  // Each line contributes: max(1, extensionCount) where extensionCount = count of (seaside, preAnodising, postforming)
  endorsementCount = computed(() => this.calculateEndorsementCount(this.lineExtensions()));

  // Computed: maximum line name length based on endorsement count
  // 12 chars for >5 endorsements (6-column layout), 24 chars for ≤5 endorsements (3-column layout)
  maxLineNameLength = computed(() => (this.endorsementCount() > 5 ? 12 : 24));

  // Computed signal to detect line names exceeding the dynamic limit
  longLineNames = computed(() => {
    const endorsed = this.lineExtensions();
    const maxLength = this.maxLineNameLength();
    return endorsed
      .filter((le: LineExtension) => le.designation && le.designation.length > maxLength)
      .map((le: LineExtension) => ({
        lineId: le.lineId,
        designation: le.designation,
        length: le.designation.length,
      }));
  });

  hasVisibleActions(): boolean {
    return this.actionMenuItems().some((item) => item.visible);
  }

  constructor() {
    // Load certificates and production site lines when license changes
    effect(() => {
      const lic = this.license();
      if (lic) {
        untracked(() => {
          this.loadCertificates();
          this.loadProductionSiteLines();
        });
      }
    });
  }

  ngOnInit(): void {
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe((user) => {
        this.currentUser.set(user);
      });

    // Load certificate tokens if available
    if (this.config?.data?.certificateTokens) {
      this.certificateTokens = this.config.data.certificateTokens;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildActionMenuItems(): ActionMenuItem[] {
    const lic = this.license();
    if (!lic) return [];

    const hasCertificates = this.certificates().length > 0;

    return [
      {
        label: hasCertificates ? 'Re-generate Certificate' : 'Generate Certificate',
        icon: 'pi pi-file-pdf',
        command: () => this.onGenerateCertificate(),
        visible: this.canGenerateCertificate(),
        danger: false,
      },
      {
        label: 'Suspend License',
        icon: 'pi pi-pause-circle',
        command: () => this.onSuspend(),
        visible: this.canSuspend(),
        danger: true,
      },
      {
        label: 'Resume License',
        icon: 'pi pi-play-circle',
        command: () => this.onResume(),
        visible: this.canResume(),
        danger: false,
      },
      {
        label: 'Withdraw License',
        icon: 'pi pi-ban',
        command: () => this.onWithdraw(),
        visible: this.canWithdraw(),
        danger: true,
      },
    ];
  }

  private loadProductionSiteLines(): void {
    const lic = this.license();
    if (!lic?.productionSiteId) {
      return;
    }

    const coatingLinesService = this.odataFactory.entitySet<CoatingLine>('CoatingLines');
    coatingLinesService
      .entities()
      .query((q) => {
        q.filter(
          `ProductionSiteId eq ${lic.productionSiteId} and IsArchived eq false and UsedForArchitecturalCoatingsOnAluminium eq true`
        );
        q.orderBy('Designation');
      })
      .fetch()
      .subscribe({
        next: (response) => {
          this.productionSiteLines.set(response.entities || []);
        },
        error: (error) => {
          console.error('Failed to load production site coating lines:', error);
        },
      });
  }

  private loadCertificates(): void {
    const lic = this.license();
    if (!lic) return;

    // Fetch actual documents for this license using OData
    const documentsService = this.odataFactory.entitySet<WorkflowDocument>('WorkflowDocuments');

    documentsService
      .entities()
      .query((q) => {
        // Filter by EntityType and EntityId for this license
        q.filter(
          `EntityType eq '${EntityTypes.License}' and EntityId eq ${lic.id} and DocumentType eq '${DocumentTypes.Certificate}'`
        );
        q.orderBy('CreatedAt desc');
      })
      .fetch()
      .subscribe({
        next: (response) => {
          if (response.entities && response.entities.length > 0) {
            this.certificates.set(response.entities);
          } else {
            // No documents found, try fallback
            this.loadCertificatesFromStatus();
          }
        },
        error: (error) => {
          console.error('Failed to load certificates:', error);
          // Fallback: create certificates based on renewal status
          this.loadCertificatesFromStatus();
        },
      });
  }

  private loadCertificatesFromStatus(): void {
    const lic = this.license();
    if (!lic) return;

    // Fallback method when documents API is not available
    const fallbackCertificates: WorkflowDocument[] = [];

    // Check if license has completed renewals or is in a state where certificates exist
    const hasCertificates =
      lic.renewalDisplayStatus === 'Certificate Ready' ||
      lic.renewalDisplayStatus === 'Certificate Sent' ||
      (lic.lastReview && ['IssuingCertificates', 'FinalReview', 'Completed'].includes(lic.lastReview.currentState));

    if (hasCertificates && lic.lastReview) {
      const currentYear = lic.lastReview.certificationYear || new Date().getFullYear();
      fallbackCertificates.push({
        id: `cert-${currentYear}`,
        entityType: EntityTypes.License,
        entityId: lic.id,
        documentType: DocumentTypes.Certificate,
        objectStoreRef: `documents/licenses/${lic.id}/certificates/${currentYear}.pdf`,
        fileName: `Certificate_${lic.completeLicenseNumber}_${currentYear}.pdf`,
        mimeType: 'application/pdf',
        createdAt: lic.lastReview.createdOn || new Date().toISOString(),
        fileSize: 0, // Unknown size in fallback
        isLocked: false,
        versionsCount: 1,
        currentVersion: 1,
      } as WorkflowDocument);
    }

    this.certificates.set(fallbackCertificates);
  }

  // Permission checks
  canExtend(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !lic.isGranting &&
      !lic.withdrawalDate &&
      !lic.suspensionDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canActivate(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      lic.isGranting === true &&
      !lic.withdrawalDate &&
      !lic.suspensionDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canWithdraw(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !lic.withdrawalDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canSuspend(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !lic.withdrawalDate &&
      !lic.suspensionDate &&
      !lic.isArchived &&
      !lic.isGranting &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canResume(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !!lic.suspensionDate &&
      !lic.withdrawalDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canEditLineEndorsements(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !lic.withdrawalDate &&
      !lic.suspensionDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canGenerateCertificate(): boolean {
    const lic = this.license();
    return (
      !!lic &&
      !lic.withdrawalDate &&
      !lic.suspensionDate &&
      !lic.isArchived &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  canRevokeToken(token: DocumentDownloadToken): boolean {
    return (
      !token.isRevoked &&
      !this.isTokenExpired(token) &&
      this.permissionService.hasEffectivePermission(this.currentUser(), 'License.Admin')
    );
  }

  // Action handlers
  onExtend(): void {
    const lic = this.license();
    if (!lic) return;

    // Import the extension component dynamically to avoid circular dependency
    import('../license-extension/license-extension.component').then(({ LicenseExtensionComponent }) => {
      const extensionDialogRef = this.dialogService.open(LicenseExtensionComponent, {
        header: `Renew License ${lic.completeLicenseNumber}`,
        position: 'right',
        height: '100%',
        width: '600px',
        modal: true,
        draggable: false,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          license: lic,
        },
      });

      extensionDialogRef?.onClose.subscribe((result: unknown) => {
        const typedResult = result as { extended?: boolean; newExpiryDate?: string } | undefined;
        if (typedResult?.extended && typedResult?.newExpiryDate) {
          // Note: Success message is already shown by the extension dialog

          // Reload the license to get updated expiry date, extension count, etc.
          const licensesOdataService = this.odataFactory.entitySet<CoaterLicense>('CoaterLicenses');
          licensesOdataService
            .entity(lic.id)
            .query((q) => {
              q.expand('ProductionSite($expand=Customer),LastReview');
            })
            .fetch()
            .subscribe({
              next: (updatedLicense) => {
                if (updatedLicense.entity) {
                  // Notify the service to trigger grid reload AND overlay update
                  this.licensesService.notifyLicenseUpdated(updatedLicense.entity);
                }
              },
              error: (error) => {
                console.error('Failed to reload license after extension:', error);
              },
            });

          // Reload certificates to show the newly generated certificate
          this.loadCertificates();
        }
      });
    });
  }

  onActivate(): void {
    const lic = this.license();
    if (!lic) return;

    // Import the activation component dynamically to avoid circular dependency
    import('../license-activation/license-activation.component').then(({ LicenseActivationComponent }) => {
      const activationDialogRef = this.dialogService.open(LicenseActivationComponent, {
        header: 'Grant License',
        position: 'right',
        height: '100%',
        width: '600px',
        modal: true,
        draggable: false,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          license: lic,
        },
      });

      activationDialogRef?.onClose.subscribe((result: unknown) => {
        const typedResult = result as { granted?: boolean; completeLicenseNumber?: string } | undefined;
        if (typedResult?.granted) {
          // Reload the license to get updated data
          const licensesOdataService = this.odataFactory.entitySet<CoaterLicense>('CoaterLicenses');
          licensesOdataService
            .entity(lic.id)
            .query((q) => {
              q.expand('ProductionSite($expand=Customer),LastReview');
            })
            .fetch()
            .subscribe({
              next: (updatedLicense) => {
                if (updatedLicense.entity) {
                  // Notify the service to trigger grid reload AND overlay update
                  this.licensesService.notifyLicenseUpdated(updatedLicense.entity);
                }
              },
              error: (error) => {
                console.error('Failed to reload license after activation:', error);
              },
            });

          // Reload certificates to show the newly generated certificate
          this.loadCertificates();
        }
      });
    });
  }

  onEditLineEndorsements(): void {
    const lic = this.license();
    if (!lic) return;

    // Import the line endorsements editor component dynamically
    import('../license-line-endorsements-editor/license-line-endorsements-editor.component').then(
      ({ LicenseLineEndorsementsEditorComponent }) => {
        const editorDialogRef = this.dialogService.open(LicenseLineEndorsementsEditorComponent, {
          header: `Edit Line Endorsements - ${lic.completeLicenseNumber}`,
          position: 'right',
          height: '100%',
          width: '700px',
          modal: true,
          draggable: false,
          closable: true,
          maskStyleClass: 'mask',
          contentStyle: { overflow: 'auto' },
          data: {
            license: lic,
          },
        });

        editorDialogRef?.onClose.subscribe((result: unknown) => {
          const typedResult = result as { updated?: boolean } | undefined;
          if (typedResult?.updated) {
            // Reload the license to get updated line endorsements
            const odataService = this.odataFactory.entitySet<CoaterLicense>('CoaterLicenses');
            odataService
              .entity(lic.id)
              .query((q) => {
                q.expand('ProductionSite($expand=Customer),LastReview');
              })
              .fetch()
              .subscribe({
                next: (updatedLicense) => {
                  if (updatedLicense.entity) {
                    // Reload production site lines to refresh mismatch detection
                    this.loadProductionSiteLines();

                    // Notify the service to trigger grid reload AND overlay update
                    this.licensesService.notifyLicenseUpdated(updatedLicense.entity);
                  }
                },
                error: (error) => {
                  console.error('Failed to reload license after updating line endorsements:', error);
                },
              });

            // Reload certificates to ensure they include updated line endorsements
            this.loadCertificates();
          }
        });
      }
    );
  }

  onGenerateCertificate(): void {
    const lic = this.license();
    if (!lic) return;

    const hasCertificates = this.certificates().length > 0;

    // Generate certificate directly without confirmation
    this.regeneratingCertificate.set(true);
    this.licensesService.regenerateCertificate(lic.id).subscribe({
      next: (result) => {
        this.regeneratingCertificate.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Certificate Generated',
          detail: hasCertificates
            ? `New certificate version ${result.version} has been generated successfully.`
            : 'Certificate has been generated successfully.',
        });
        // Reload certificates to show the new certificate
        this.loadCertificates();
      },
      error: (error) => {
        this.regeneratingCertificate.set(false);
        console.error('Failed to generate certificate:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Certificate Generation Failed',
          detail: error.error?.message || 'Failed to generate certificate. Please try again.',
        });
      },
    });
  }

  onWithdraw(): void {
    const lic = this.license();
    if (!lic) return;

    // Import the withdrawal component dynamically
    import('../license-withdrawal/license-withdrawal.component').then(({ LicenseWithdrawalComponent }) => {
      const withdrawalDialogRef = this.dialogService.open(LicenseWithdrawalComponent, {
        header: `Withdraw License ${lic.completeLicenseNumber}`,
        position: 'right',
        height: '100%',
        width: '600px',
        modal: true,
        draggable: false,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          license: lic,
        },
      });

      withdrawalDialogRef?.onClose.subscribe((updatedLicense: CoaterLicense) => {
        console.log('Withdrawal dialog closed with:', updatedLicense);
        if (updatedLicense) {
          // Notify the service to trigger grid reload AND overlay update
          this.licensesService.notifyLicenseUpdated(updatedLicense);

          this.messageService.add({
            severity: 'success',
            summary: 'License Withdrawn',
            detail: `License ${updatedLicense.completeLicenseNumber} has been withdrawn`,
            life: 3000,
          });
        }
      });
    });
  }

  onSuspend(): void {
    const lic = this.license();
    if (!lic) return;

    import('../license-suspension/license-suspension.component').then(({ LicenseSuspensionComponent }) => {
      const suspensionDialogRef = this.dialogService.open(LicenseSuspensionComponent, {
        header: `Suspend License ${lic.completeLicenseNumber}`,
        position: 'right',
        height: '100%',
        width: '600px',
        modal: true,
        draggable: false,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          license: lic,
        },
      });

      suspensionDialogRef?.onClose.subscribe((result: boolean | undefined) => {
        if (result) {
          // Reload the license to get updated data
          const licensesOdataService = this.odataFactory.entitySet<CoaterLicense>('CoaterLicenses');
          licensesOdataService
            .entity(lic.id)
            .query((q) => {
              q.expand('ProductionSite($expand=Customer),LastReview');
            })
            .fetch()
            .subscribe({
              next: (updatedLicense) => {
                if (updatedLicense.entity) {
                  this.licensesService.notifyLicenseUpdated(updatedLicense.entity);
                }
              },
              error: (error) => {
                console.error('Failed to reload license after suspension:', error);
              },
            });
        }
      });
    });
  }

  onResume(): void {
    const lic = this.license();
    if (!lic) return;

    import('../license-resume/license-resume.component').then(({ LicenseResumeComponent }) => {
      const resumeDialogRef = this.dialogService.open(LicenseResumeComponent, {
        header: `Resume License ${lic.completeLicenseNumber}`,
        position: 'right',
        height: '100%',
        width: '600px',
        modal: true,
        draggable: false,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          license: lic,
        },
      });

      resumeDialogRef?.onClose.subscribe((result: boolean | undefined) => {
        if (result) {
          // Reload the license to get updated data
          const licensesOdataService = this.odataFactory.entitySet<CoaterLicense>('CoaterLicenses');
          licensesOdataService
            .entity(lic.id)
            .query((q) => {
              q.expand('ProductionSite($expand=Customer),LastReview');
            })
            .fetch()
            .subscribe({
              next: (updatedLicense) => {
                if (updatedLicense.entity) {
                  this.licensesService.notifyLicenseUpdated(updatedLicense.entity);
                }
              },
              error: (error) => {
                console.error('Failed to reload license after resumption:', error);
              },
            });
        }
      });
    });
  }

  onEditProductionSite(): void {
    const lic = this.license();
    if (!lic?.productionSite) {
      console.error('No production site available to edit');
      return;
    }

    const productionSite = lic.productionSite;

    // Import the production site form component dynamically
    import('../../../../crm/pages/organisation/production-sites/production-site-form/production-site-form.component').then(
      ({ ProductionSiteFormComponent }) => {
        // First, fetch the full production site WITH coating lines
        const productionSitesService = this.odataFactory.entitySet<ProductionSite>('ProductionSites');
        productionSitesService
          .entity(productionSite.id)
          .query((q) => {
            q.expand('CoatingLines');
          })
          .fetch()
          .subscribe({
            next: (productionSiteResponse) => {
              const fullProductionSite = productionSiteResponse.entity;

              if (!fullProductionSite) {
                console.error('Production site not found');
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Production site not found.',
                });
                return;
              }

              // Then fetch contacts for the customer organization
              const contactsService = this.odataFactory.entitySet<Contact>('Contacts');
              contactsService
                .entities()
                .query((q) => {
                  q.filter(`OrganisationId eq ${productionSite.customerId}`);
                  q.select('Id,FirstName,LastName,Email');
                  q.orderBy('LastName,FirstName');
                })
                .fetch()
                .subscribe({
                  next: (contactsResponse) => {
                    const contactsForSelect = [
                      { label: 'None', value: null },
                      ...(contactsResponse.entities || []).map((contact: Contact) => ({
                        label: `${contact.firstName} ${contact.lastName}`,
                        value: contact.id,
                      })),
                    ];

                    // Open the production site form dialog with full data
                    const dialogRef = this.dialogService.open(ProductionSiteFormComponent, {
                      header: `Edit Production Site: ${fullProductionSite.name}`,
                      position: 'right',
                      height: '100%',
                      width: '700px',
                      modal: true,
                      draggable: false,
                      closable: true,
                      maskStyleClass: 'mask',
                      contentStyle: { overflow: 'auto' },
                      data: {
                        productionSite: fullProductionSite,
                        contactsForSelect: contactsForSelect,
                      },
                    });

                    dialogRef?.onClose.subscribe((result) => {
                      if (result) {
                        // Save the updated production site to the backend
                        this.productionSiteDataService
                          .update(productionSite.id, result)
                          .pipe(take(1))
                          .subscribe({
                            next: () => {
                              // Reload the production site lines
                              this.loadProductionSiteLines();

                              // Show success message
                              this.messageService.add({
                                severity: 'success',
                                summary: 'Production Site Updated',
                                detail: 'Production site lines have been updated successfully.',
                              });

                              // Trigger license reload to get fresh production site data
                              this.licensesService.triggerReload();
                            },
                            error: (error) => {
                              console.error('Failed to update production site:', error);
                              this.messageService.add({
                                severity: 'error',
                                summary: 'Update Failed',
                                detail: 'Failed to update production site lines. Please try again.',
                              });
                            },
                          });
                      }
                    });
                  },
                  error: (error) => {
                    console.error('Failed to load contacts for production site:', error);

                    // Still open the dialog even if contacts failed to load
                    const dialogRef = this.dialogService.open(ProductionSiteFormComponent, {
                      header: `Edit Production Site: ${fullProductionSite.name}`,
                      position: 'right',
                      height: '100%',
                      width: '700px',
                      modal: true,
                      draggable: false,
                      closable: true,
                      maskStyleClass: 'mask',
                      contentStyle: { overflow: 'auto' },
                      data: {
                        productionSite: fullProductionSite,
                        contactsForSelect: [],
                      },
                    });

                    dialogRef?.onClose.subscribe((result) => {
                      if (result) {
                        // Save the updated production site to the backend
                        this.productionSiteDataService
                          .update(productionSite.id, result)
                          .pipe(take(1))
                          .subscribe({
                            next: () => {
                              // Reload the production site lines
                              this.loadProductionSiteLines();

                              // Show success message
                              this.messageService.add({
                                severity: 'success',
                                summary: 'Production Site Updated',
                                detail: 'Production site lines have been updated successfully.',
                              });

                              // Trigger license reload to get fresh production site data
                              this.licensesService.triggerReload();
                            },
                            error: (error) => {
                              console.error('Failed to update production site:', error);
                              this.messageService.add({
                                severity: 'error',
                                summary: 'Update Failed',
                                detail: 'Failed to update production site lines. Please try again.',
                              });
                            },
                          });
                      }
                    });
                  },
                });
            },
            error: (error) => {
              console.error('Failed to load production site with coating lines:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Failed to Load Production Site',
                detail: 'Could not load production site data. Please try again.',
              });
            },
          });
      }
    );
  }

  // Token helpers
  isTokenExpired(token: DocumentDownloadToken): boolean {
    const expiresOn = new Date(token.expiresOn);
    return expiresOn < new Date();
  }

  getTokenStatus(token: DocumentDownloadToken): string {
    if (token.isRevoked) return 'Revoked';
    if (this.isTokenExpired(token)) return 'Expired';
    if (token.lastDownloadedOn) return 'Downloaded';
    return 'Active';
  }

  getTokenStatusSeverity(token: DocumentDownloadToken): 'success' | 'info' | 'warn' | 'danger' {
    if (token.isRevoked) return 'danger';
    if (this.isTokenExpired(token)) return 'warn';
    if (token.lastDownloadedOn) return 'info';
    return 'success';
  }

  getLicenseStatus(): { label: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' } {
    // Match backend LicenseState computation logic
    const lic = this.license();
    if (!lic) return { label: 'Unknown', severity: 'secondary' };

    switch (lic.licenseState) {
      case 'Valid':
        return { label: 'Valid', severity: 'success' };
      case 'Expiring':
        return { label: 'Expiring', severity: 'warn' };
      case 'Granting':
        return { label: 'Granting', severity: 'info' };
      case 'Expired':
        return { label: 'Expired', severity: 'danger' };
      case 'Withdrawn':
        return { label: 'Withdrawn', severity: 'danger' };
      case 'Suspended':
        return { label: 'Suspended', severity: 'warn' };
      default:
        break;
    }
    return { label: 'Unknown', severity: 'secondary' };
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return '-';
    return this.datePipe.transform(date, 'dd.MM.yyyy', 'UTC') || '-';
  }

  formatFileSize(bytes: number): string {
    return this.documentService.formatFileSize(bytes);
  }

  getCertificationYear(certificate: WorkflowDocument): string {
    if (!certificate.metadataJson) return '-';
    try {
      const metadata = JSON.parse(certificate.metadataJson);
      return metadata.CertificationYear || '-';
    } catch {
      return '-';
    }
  }

  getPretreatmentType(line: LineExtension): string {
    if (line.pretreatment === 'Chromate') return 'Chromate';
    if (line.pretreatment === 'Chemical') return 'Chemical';
    if (line.pretreatment === 'PreAnodising') return 'Pre-Anodising';
    return 'None';
  }

  /**
   * Calculate total endorsement count from line extensions.
   * Each line contributes: max(1, extensionCount) where extensionCount = count of (seaside, preAnodising, postforming).
   */
  private calculateEndorsementCount(lines: LineExtension[]): number {
    return lines.reduce((count, le) => {
      const extensionCount = [le.seaside, le.pretreatment === PretreatmentType.PreAnodising, le.postforming].filter(
        Boolean
      ).length;
      return count + Math.max(1, extensionCount);
    }, 0);
  }

  // Certificate action methods
  viewCertificate(certificate: WorkflowDocument): void {
    if (certificate.objectStoreRef) {
      this.documentService.viewDocument(
        certificate.objectStoreRef,
        certificate.fileName,
        certificate.fileName,
        certificate.mimeType
      );
    }
  }

  downloadCertificate(certificate: WorkflowDocument): void {
    if (certificate.objectStoreRef) {
      this.documentService.downloadDocument(certificate.objectStoreRef, certificate.fileName);
    }
  }

  downloadCertificateInLanguage(language: string): void {
    const lic = this.license();
    if (!lic) return;

    const downloadUrl = `/api/v1/licenses/${lic.id}/certificate?language=${language}`;
    const fileName = `Certificate_${lic.completeLicenseNumber}_${language.toUpperCase()}.pdf`;

    this.documentService.downloadFromUrl(downloadUrl, fileName);
  }

  toggleCertificateTokens(certificateId: string): void {
    if (this.expandedCertificateTokens() === certificateId) {
      this.expandedCertificateTokens.set(null);
    } else {
      this.expandedCertificateTokens.set(certificateId);
    }
  }

  onCertificateTokensUpdated(): void {
    // Refresh certificates when tokens are updated
    this.loadCertificates();
  }

  openMap(): void {
    const lic = this.license();
    if (!lic?.productionSite) return;

    // Build the address string for Google Maps
    const addressParts = [];
    const site = lic.productionSite;

    if (site.addressLine1) addressParts.push(site.addressLine1);
    if (site.addressLine2) addressParts.push(site.addressLine2);
    if (site.addressLine3) addressParts.push(site.addressLine3);
    if (site.postalCode) addressParts.push(site.postalCode);
    if (site.city) addressParts.push(site.city);
    if (site.state) addressParts.push(site.state);
    if (site.countryName) addressParts.push(site.countryName);

    const fullAddress = addressParts.join(', ');
    const encodedAddress = encodeURIComponent(fullAddress);

    // Open Google Maps in a new tab
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  }

  async sendEmail(email: string | null | undefined): Promise<void> {
    if (email) {
      try {
        await navigator.clipboard.writeText(email);
        this.messageService.add({
          severity: 'success',
          summary: 'Email Copied',
          detail: `${email} has been copied to clipboard`,
          life: 3000,
        });
      } catch (err) {
        console.error('Failed to copy email:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Copy Failed',
          detail: 'Please copy the email manually',
          life: 3000,
        });
      }
    }
  }
}
